import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Users, FileText, Plus, Edit, Trash2, Save, X, LogOut, Lock, Sun, Moon, Truck, Download } from 'lucide-react';
import Login from './Login';
import Register from './Register';
import UserManagement from './UserManagement';
import ChangePassword from './ChangePassword';
import './App.css';
import * as XLSX from 'xlsx-js-style';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [activeTab, setActiveTab] = useState('rmas');
  const [produtos, setProdutos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [rmas, setRmas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [pastas, setPastas] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loteAtivo, setLoteAtivo] = useState('todos');
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [editingLote, setEditingLote] = useState(null);
  const [loteForm, setLoteForm] = useState({ nome: '', descricao: '' });
  const [filteredRmas, setFilteredRmas] = useState([]);
  const [filterCliente, setFilterCliente] = useState('');
  const [filterNotaFiscal, setFilterNotaFiscal] = useState('');
  const [filterProduto, setFilterProduto] = useState('');
  const [filterEan, setFilterEan] = useState('');
  const [filterMarca, setFilterMarca] = useState('');
  const [filterModelo, setFilterModelo] = useState('');
  const [filterNomeProduto, setFilterNomeProduto] = useState('');
  const [filterCliente2, setFilterCliente2] = useState('');
  const [filterFornecedor, setFilterFornecedor] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [viewItem, setViewItem] = useState(null);
  const [viewType, setViewType] = useState(null);
  const [viewIndex, setViewIndex] = useState(null);

  // Tema: persiste no localStorage e aplica o atributo no <html>
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      setIsAuthenticated(true);
      loadData();
    }
  }, []);

  useEffect(() => {
    let filtered = rmas;

    if (loteAtivo !== 'todos') {
      filtered = filtered.filter(rma => String(rma.lote_id) === loteAtivo);
    }

    if (filterCliente) {
      filtered = filtered.filter(rma =>
        rma.cliente_nome && rma.cliente_nome.toLowerCase().includes(filterCliente.toLowerCase())
      );
    }

    if (filterNotaFiscal) {
      filtered = filtered.filter(rma =>
        rma.nota_fiscal && rma.nota_fiscal.toLowerCase().includes(filterNotaFiscal.toLowerCase())
      );
    }

    if (filterProduto) {
      filtered = filtered.filter(rma =>
        rma.produto_nome && rma.produto_nome.toLowerCase().includes(filterProduto.toLowerCase())
      );
    }

    setFilteredRmas(filtered);
  }, [rmas, loteAtivo, filterCliente, filterNotaFiscal, filterProduto]);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    loadData();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setProdutos([]);
    setClientes([]);
    setRmas([]);
  };

  const handleRegister = () => {
    setShowRegister(false);
  };

  const loadData = async () => {
    try {
      const [prodRes, cliRes, rmaRes, fornRes, lotesRes] = await Promise.all([
        axios.get(`${API_URL}/produtos`, authHeaders()),
        axios.get(`${API_URL}/clientes`, authHeaders()),
        axios.get(`${API_URL}/rmas`, authHeaders()),
        axios.get(`${API_URL}/fornecedores`, authHeaders()),
        axios.get(`${API_URL}/lotes`, authHeaders())
      ]);
      setProdutos(prodRes.data);
      setClientes(cliRes.data);
      setRmas(rmaRes.data);
      setFornecedores(fornRes.data);
      setPastas([]);
      setLotes(lotesRes.data);
      // Seleciona o primeiro lote automaticamente se nenhum estiver ativo
      if (lotesRes.data.length > 0 && loteAtivo === 'todos') {
        setLoteAtivo(String(lotesRes.data[0].id));
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    }
  };

  const handleSave = async (type) => {
    try {
      // Garante status padrão para RMAs
      const dataToSave = type === 'rmas' ? { status: 'aberto', ...formData } : formData;
      if (editingItem) {
        await axios.put(`${API_URL}/${type}/${editingItem.id}`, dataToSave, authHeaders());
      } else {
        await axios.post(`${API_URL}/${type}`, dataToSave, authHeaders());
      }
      setShowModal(false);
      setEditingItem(null);
      setFormData({});
      loadData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        alert('Erro ao salvar dados');
      }
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Tem certeza que deseja deletar?')) return;
    try {
      await axios.delete(`${API_URL}/${type}/${id}`, authHeaders());
      loadData();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        alert('Erro ao deletar');
      }
    }
  };

  const openModal = (type, item = null) => {
    setEditingItem(item);
    // Se estiver num lote ativo e for novo RMA, pré-preenche o lote
    let defaultData = item ? { ...item } : (type === 'rmas' && loteAtivo !== 'todos' ? { lote_id: parseInt(loteAtivo) } : {});
    // Garante status padrão para RMAs
    if (type === 'rmas' && !defaultData.status) defaultData.status = 'aberto';
    setFormData(defaultData);
    setShowModal(true);
  };

  const openView = (type, item, index = null) => {
    setViewItem(item);
    setViewType(type);
    setViewIndex(index);
  };

  const closeView = () => {
    setViewItem(null);
    setViewType(null);
    setViewIndex(null);
  };

  const saveLote = async () => {
    if (!loteForm.nome.trim()) return;
    try {
      if (editingLote) {
        await axios.put(`${API_URL}/lotes/${editingLote.id}`, loteForm, authHeaders());
      } else {
        await axios.post(`${API_URL}/lotes`, loteForm, authHeaders());
      }
      setShowLoteModal(false);
      setEditingLote(null);
      setLoteForm({ nome: '', descricao: '' });
      loadData();
    } catch (e) { alert('Erro ao salvar lote'); }
  };

  const deleteLote = async (id) => {
    if (!window.confirm('Deletar lote? Os RMAs do lote não serão deletados.')) return;
    await axios.delete(`${API_URL}/lotes/${id}`, authHeaders());
    if (loteAtivo === String(id)) setLoteAtivo('todos');
    loadData();
  };

  const exportarExcel = (lote, rmasDoLote) => {
    const colunas = ['Nº', 'Cliente', 'Produto', 'Marca', 'Modelo', 'EAN', 'Fornecedor', 'Nº Série', 'Nota Fiscal', 'Status', 'Data Abertura', 'Diagnóstico', 'Solução'];
    const tituloRow = [{ v: `Relatório de RMAs — ${lote}`, t: 's', s: { font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0066CC' } }, alignment: { horizontal: 'center' } } }];
    const blankRow = [{ v: '', t: 's' }];
    const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1a1e3f' } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: '0066CC' } } } };
    const headerRow = colunas.map(c => ({ v: c, t: 's', s: headerStyle }));
    const dataRows = rmasDoLote.map((r, i) => {
      const rowStyle = { fill: { fgColor: { rgb: i % 2 === 0 ? 'EEF2F7' : 'FFFFFF' } }, alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } };
      return [
        { v: `#${i + 1}`, t: 's', s: { ...rowStyle, alignment: { horizontal: 'center' } } },
        { v: r.cliente_nome || '-', t: 's', s: rowStyle },
        { v: r.produto_nome || '-', t: 's', s: rowStyle },
        { v: r.produto_marca || '-', t: 's', s: rowStyle },
        { v: r.produto_modelo || '-', t: 's', s: rowStyle },
        { v: r.produto_ean || '-', t: 's', s: rowStyle },
        { v: r.fornecedor_nome || '-', t: 's', s: rowStyle },
        { v: r.numero_serie || '-', t: 's', s: rowStyle },
        { v: r.nota_fiscal || '-', t: 's', s: rowStyle },
        { v: r.status || '-', t: 's', s: rowStyle },
        { v: r.data_abertura ? new Date(r.data_abertura).toLocaleDateString('pt-BR') : '-', t: 's', s: { ...rowStyle, alignment: { horizontal: 'center' } } },
        { v: r.diagnostico || '-', t: 's', s: rowStyle },
        { v: r.solucao || '-', t: 's', s: rowStyle },
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([tituloRow, blankRow, headerRow, ...dataRows]);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colunas.length - 1 } }];
    // Altura das linhas: título, branco, cabeçalho, dados
    ws['!rows'] = [
      { hpt: 30 }, // título
      { hpt: 6 },  // branco
      { hpt: 20 }, // cabeçalho
      ...rmasDoLote.map(() => ({ hpt: 18 })) // dados
    ];
    ws['!cols'] = [
      { wch: 5 }, { wch: 22 }, { wch: 22 }, { wch: 15 }, { wch: 15 }, { wch: 16 }, { wch: 18 },
      { wch: 15 }, { wch: 16 }, { wch: 12 }, { wch: 13 }, { wch: 38 }, { wch: 38 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RMAs');
    XLSX.writeFile(wb, `RMAs - ${lote}.xlsx`);
  };

  if (!isAuthenticated) {
    return showRegister ? (
      <Register onRegister={handleRegister} onShowLogin={() => setShowRegister(false)} />
    ) : (
      <Login onLogin={handleLogin} onShowRegister={() => setShowRegister(true)} />
    );
  }

  // Verifica se o usuário pode criar/editar (admin ou editor)
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  // Verifica se o usuário pode excluir (apenas admin)
  const canDelete = user?.role === 'admin';

  const renderForm = (type) => {
    switch (type) {
      case 'produtos':
        return (
          <>
            <div className="form-group">
              <label>Nome do Produto *</label>
              <input
                type="text"
                value={formData.nome || ''}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Modelo</label>
              <input
                type="text"
                value={formData.modelo || ''}
                onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Marca</label>
              <input
                type="text"
                value={formData.marca || ''}
                onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>EAN (Código de Barras)</label>
              <input
                type="text"
                value={formData.ean || ''}
                onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                placeholder="Ex: 7891234567890"
                maxLength={14}
              />
            </div>
            <div className="form-group">
              <label>Preço</label>
              <input
                type="number"
                step="0.01"
                value={formData.preco || ''}
                onChange={(e) => setFormData({ ...formData, preco: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Descrição</label>
              <textarea
                value={formData.descricao || ''}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                rows="3"
              />
            </div>
          </>
        );
      case 'clientes':
        return (
          <>
            <div className="form-group">
              <label>Nome do Cliente *</label>
              <input
                type="text"
                value={formData.nome || ''}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Telefone</label>
              <input
                type="text"
                value={formData.telefone || ''}
                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Endereço</label>
              <textarea
                value={formData.endereco || ''}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                rows="3"
              />
            </div>
          </>
        );
      case 'rmas':
        return (
          <>
            <div className="form-group">
              <label>Cliente</label>
              <select
                value={formData.cliente_id || ''}
                onChange={(e) => setFormData({ ...formData, cliente_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Selecione um cliente</option>
                {clientes.map((cli) => (
                  <option key={cli.id} value={cli.id}>{cli.nome}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Produto *</label>
              <select
                value={formData.produto_id || ''}
                onChange={(e) => setFormData({ ...formData, produto_id: parseInt(e.target.value) })}
                required
              >
                <option value="">Selecione um produto</option>
                {produtos.map((prod) => (
                  <option key={prod.id} value={prod.id}>{prod.nome} - {prod.modelo}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Número de Série</label>
              <input
                type="text"
                value={formData.numero_serie || ''}
                onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Número da Nota Fiscal</label>
              <input
                type="text"
                value={formData.nota_fiscal || ''}
                onChange={(e) => setFormData({ ...formData, nota_fiscal: e.target.value })}
                placeholder="00000000000000"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status || 'aberto'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="aberto">Aberto</option>
                <option value="em_analise">Em Análise</option>
                <option value="aprovado">Aprovado</option>
                <option value="reprovado">Reprovado</option>
                <option value="concluido">Concluído</option>
              </select>
            </div>
            <div className="form-group">
              <label>Fornecedor</label>
              <select
                value={formData.fornecedor_id || ''}
                onChange={(e) => setFormData({ ...formData, fornecedor_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Selecione um fornecedor (opcional)</option>
                {fornecedores.map((forn) => (
                  <option key={forn.id} value={forn.id}>{forn.nome}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Lote</label>
              <select
                value={formData.lote_id || ''}
                onChange={(e) => setFormData({ ...formData, lote_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Sem lote (opcional)</option>
                {lotes.map((l) => (
                  <option key={l.id} value={l.id}>{l.nome}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Diagnóstico</label>
              <textarea
                value={formData.diagnostico || ''}
                onChange={(e) => setFormData({ ...formData, diagnostico: e.target.value })}
                rows="4"
                placeholder="Descreva o problema encontrado..."
              />
            </div>
            <div className="form-group">
              <label>Solução</label>
              <textarea
                value={formData.solucao || ''}
                onChange={(e) => setFormData({ ...formData, solucao: e.target.value })}
                rows="4"
                placeholder="Descreva a solução aplicada..."
              />
            </div>
          </>
        );
      case 'fornecedores':
        return (
          <>
            <div className="form-group">
              <label>Nome do Fornecedor *</label>
              <input
                type="text"
                value={formData.nome || ''}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>CNPJ</label>
              <input
                type="text"
                value={formData.cnpj || ''}
                onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div className="form-group">
              <label>Contato</label>
              <input
                type="text"
                value={formData.contato || ''}
                onChange={(e) => setFormData({ ...formData, contato: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Telefone</label>
              <input
                type="text"
                value={formData.telefone || ''}
                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <img src="/logo.png" alt="Logo" className="logo" />
            <div>
              <h1>Sistema de RMA</h1>
              <p>Gerenciamento de Devoluções e Reparos</p>
            </div>
          </div>
          <div className="user-info">
            <span>{user?.nome}</span>
            <button onClick={toggleTheme} className="btn-theme" title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {user?.role === 'admin' && (
              <button onClick={() => setShowUserManagement(true)} className="btn-admin">
                <Users size={18} />
                Usuários
              </button>
            )}
            <button onClick={() => setShowChangePassword(true)} className="btn-admin">
              <Lock size={18} />
              Alterar Senha
            </button>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut size={18} />
              Sair
            </button>
          </div>
        </div>
      </header>

      <nav className="nav">
        <button
          className={activeTab === 'rmas' ? 'active' : ''}
          onClick={() => setActiveTab('rmas')}
        >
          <FileText size={18} />
          RMAs
        </button>
        <button
          className={activeTab === 'produtos' ? 'active' : ''}
          onClick={() => setActiveTab('produtos')}
        >
          <Package size={18} />
          Produtos
        </button>
        <button
          className={activeTab === 'clientes' ? 'active' : ''}
          onClick={() => setActiveTab('clientes')}
        >
          <Users size={18} />
          Clientes
        </button>
        <button
          className={activeTab === 'fornecedores' ? 'active' : ''}
          onClick={() => setActiveTab('fornecedores')}
        >
          <Truck size={18} />
          Fornecedores
        </button>
      </nav>

      <main className="main">
        <div className="section-header">
          <h2>
            {activeTab === 'rmas' && (lotes.find(l => String(l.id) === loteAtivo)?.nome || 'Selecione um lote')}
            {activeTab === 'produtos' && 'Produtos Cadastrados'}
            {activeTab === 'clientes' && 'Clientes Cadastrados'}
            {activeTab === 'fornecedores' && 'Fornecedores Cadastrados'}
          </h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {activeTab === 'rmas' && loteAtivo !== 'todos' && (
              <button className="btn-primary" onClick={() => {
                const lote = lotes.find(l => String(l.id) === loteAtivo);
                const rmasDoLote = filteredRmas;
                exportarExcel(lote?.nome || 'Lote', rmasDoLote);
              }}>
                <Download size={16} /> Excel
              </button>
            )}
            {canEdit && (
              <button className="btn-primary" onClick={() => openModal(activeTab)}>
                <Plus size={18} />
                Novo
              </button>
            )}
          </div>
        </div>

        {activeTab === 'rmas' && (
          <div className="lotes-bar">
            {lotes.map(l => (
              <button
                key={l.id}
                className={loteAtivo === String(l.id) ? 'lote-btn lote-btn-active' : 'lote-btn'}
                onClick={() => setLoteAtivo(String(l.id))}
              >
                {l.nome}
                {canEdit && (
                  <span className="lote-btn-edit" onClick={e => { e.stopPropagation(); setEditingLote(l); setLoteForm({ nome: l.nome, descricao: l.descricao || '' }); setShowLoteModal(true); }}>
                    <Edit size={11} />
                  </span>
                )}
              </button>
            ))}
            {canEdit && (
              <button className="lote-btn lote-btn-new" onClick={() => { setEditingLote(null); setLoteForm({ nome: '', descricao: '' }); setShowLoteModal(true); }}>
                <Plus size={14} /> Novo Lote
              </button>
            )}
          </div>
        )}

        {activeTab === 'rmas' && (
          <div className="filters-container">
            <input
              type="text"
              placeholder="Filtrar por cliente..."
              value={filterCliente}
              onChange={(e) => setFilterCliente(e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Filtrar por nota fiscal..."
              value={filterNotaFiscal}
              onChange={(e) => setFilterNotaFiscal(e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Filtrar por produto..."
              value={filterProduto}
              onChange={(e) => setFilterProduto(e.target.value)}
              className="filter-input"
            />
          </div>
        )}

        {activeTab === 'produtos' && (
          <div className="filters-container">
            <input type="text" placeholder="Filtrar por nome..." value={filterNomeProduto} onChange={(e) => setFilterNomeProduto(e.target.value)} className="filter-input" />
            <input type="text" placeholder="Filtrar por marca..." value={filterMarca} onChange={(e) => setFilterMarca(e.target.value)} className="filter-input" />
            <input type="text" placeholder="Filtrar por modelo..." value={filterModelo} onChange={(e) => setFilterModelo(e.target.value)} className="filter-input" />
            <input type="text" placeholder="Filtrar por EAN..." value={filterEan} onChange={(e) => setFilterEan(e.target.value)} className="filter-input" />
          </div>
        )}

        {activeTab === 'clientes' && (
          <div className="filters-container">
            <input type="text" placeholder="Filtrar por nome..." value={filterCliente2} onChange={(e) => setFilterCliente2(e.target.value)} className="filter-input" />
          </div>
        )}

        {activeTab === 'fornecedores' && (
          <div className="filters-container">
            <input type="text" placeholder="Filtrar por nome..." value={filterFornecedor} onChange={(e) => setFilterFornecedor(e.target.value)} className="filter-input" />
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              {activeTab === 'rmas' && (
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Produto</th>
                  <th>Fornecedor</th>
                  <th>Nº Série</th>
                  <th>Nº Nota Fiscal</th>
                  <th>Status</th>
                  <th>Data Abertura</th>
                  <th>Ações</th>
                </tr>
              )}
              {activeTab === 'produtos' && (
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Modelo</th>
                  <th>Marca</th>
                  <th>EAN</th>
                  <th>Preço</th>
                  <th>Ações</th>
                </tr>
              )}
              {activeTab === 'clientes' && (
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Ações</th>
                </tr>
              )}
              {activeTab === 'fornecedores' && (
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Contato</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Ações</th>
                </tr>
              )}
            </thead>
            <tbody>
              {activeTab === 'rmas' && filteredRmas.map((rma, index) => (
                <tr key={rma.id} onClick={() => openView('rmas', rma, index + 1)} style={{ cursor: 'pointer' }}>
                  <td>#{index + 1}</td>
                  <td>{rma.cliente_nome || '-'}</td>
                  <td>{rma.produto_nome || '-'}</td>
                  <td>{rma.fornecedor_nome || '-'}</td>
                  <td>{rma.numero_serie || '-'}</td>
                  <td>{rma.nota_fiscal || '-'}</td>
                  <td>
                    <span className={`status status-${rma.status}`}>
                      {rma.status || 'aberto'}
                    </span>
                  </td>
                  <td>{new Date(rma.data_abertura).toLocaleDateString('pt-BR')}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <button onClick={() => openModal('rmas', rma)}>
                        <Edit size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete('rmas', rma.id)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {activeTab === 'produtos' && produtos
                .filter(prod => !filterNomeProduto || prod.nome.toLowerCase().includes(filterNomeProduto.toLowerCase()))
                .filter(prod => !filterMarca || (prod.marca && prod.marca.toLowerCase().includes(filterMarca.toLowerCase())))
                .filter(prod => !filterModelo || (prod.modelo && prod.modelo.toLowerCase().includes(filterModelo.toLowerCase())))
                .filter(prod => !filterEan || (prod.ean && prod.ean.toLowerCase().includes(filterEan.toLowerCase())))
                .map((prod) => (
                <tr key={prod.id} onClick={() => openView('produtos', prod)} style={{ cursor: 'pointer' }}>
                  <td>#{prod.id}</td>
                  <td>{prod.nome}</td>
                  <td>{prod.modelo || '-'}</td>
                  <td>{prod.marca || '-'}</td>
                  <td>{prod.ean || '-'}</td>
                  <td>R$ {prod.preco ? parseFloat(prod.preco).toFixed(2) : '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <button onClick={() => openModal('produtos', prod)}>
                        <Edit size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete('produtos', prod.id)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {activeTab === 'clientes' && clientes
                .filter(cli => !filterCliente2 || cli.nome.toLowerCase().includes(filterCliente2.toLowerCase()))
                .map((cli) => (
                <tr key={cli.id} onClick={() => openView('clientes', cli)} style={{ cursor: 'pointer' }}>
                  <td>#{cli.id}</td>
                  <td>{cli.nome}</td>
                  <td>{cli.email || '-'}</td>
                  <td>{cli.telefone || '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <button onClick={() => openModal('clientes', cli)}>
                        <Edit size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete('clientes', cli.id)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {activeTab === 'fornecedores' && fornecedores
                .filter(forn => !filterFornecedor || forn.nome.toLowerCase().includes(filterFornecedor.toLowerCase()))
                .map((forn) => (
                <tr key={forn.id} onClick={() => openView('fornecedores', forn)} style={{ cursor: 'pointer' }}>
                  <td>#{forn.id}</td>
                  <td>{forn.nome}</td>
                  <td>{forn.cnpj || '-'}</td>
                  <td>{forn.contato || '-'}</td>
                  <td>{forn.email || '-'}</td>
                  <td>{forn.telefone || '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <button onClick={() => openModal('fornecedores', forn)}>
                        <Edit size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete('fornecedores', forn.id)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {editingItem ? 'Editar' : 'Novo'}{' '}
                {activeTab === 'rmas' && 'RMA'}
                {activeTab === 'produtos' && 'Produto'}
                {activeTab === 'clientes' && 'Cliente'}
                {activeTab === 'fornecedores' && 'Fornecedor'}
              </h3>
              <button onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(activeTab); }}>
              <div className="modal-body">
                {renderForm(activeTab)}
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={18} />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewItem && viewType && (
        <div className="modal-overlay" onClick={closeView}>
          <div className="modal modal-view" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {viewType === 'rmas' && `RMA #${viewIndex || viewItem.id}`}
                {viewType === 'produtos' && `Produto #${viewItem.id}`}
                {viewType === 'clientes' && `Cliente #${viewItem.id}`}
                {viewType === 'fornecedores' && `Fornecedor #${viewItem.id}`}
              </h3>
              <button onClick={closeView}><X size={20} /></button>
            </div>
            <div className="modal-body view-body">
              {viewType === 'rmas' && (
                <>
                  <div className="view-grid">
                    <div className="view-field">
                      <span className="view-label">Cliente</span>
                      <span className="view-value">{viewItem.cliente_nome || '-'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Produto</span>
                      <span className="view-value">{viewItem.produto_nome || '-'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Marca</span>
                      <span className="view-value">{viewItem.produto_marca || '-'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Modelo</span>
                      <span className="view-value">{viewItem.produto_modelo || '-'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Número de Série</span>
                      <span className="view-value">{viewItem.numero_serie || '-'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Nota Fiscal</span>
                      <span className="view-value">{viewItem.nota_fiscal || '-'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Status</span>
                      <span className={`status status-${viewItem.status}`}>{viewItem.status || 'aberto'}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Data de Abertura</span>
                      <span className="view-value">{new Date(viewItem.data_abertura).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <div className="view-field">
                      <span className="view-label">Fornecedor</span>
                      <span className="view-value">{viewItem.fornecedor_nome || '-'}</span>
                    </div>
                  </div>
                  {viewItem.diagnostico && (
                    <div className="view-field view-field-full">
                      <span className="view-label">Diagnóstico</span>
                      <span className="view-value">{viewItem.diagnostico}</span>
                    </div>
                  )}
                  {viewItem.solucao && (
                    <div className="view-field view-field-full">
                      <span className="view-label">Solução</span>
                      <span className="view-value">{viewItem.solucao}</span>
                    </div>
                  )}
                </>
              )}
              {viewType === 'produtos' && (
                <div className="view-grid">
                  <div className="view-field">
                    <span className="view-label">Nome</span>
                    <span className="view-value">{viewItem.nome}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Modelo</span>
                    <span className="view-value">{viewItem.modelo || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Marca</span>
                    <span className="view-value">{viewItem.marca || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">EAN</span>
                    <span className="view-value">{viewItem.ean || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Preço</span>
                    <span className="view-value">R$ {viewItem.preco ? parseFloat(viewItem.preco).toFixed(2) : '-'}</span>
                  </div>
                  {viewItem.descricao && (
                    <div className="view-field">
                      <span className="view-label">Descrição</span>
                      <span className="view-value">{viewItem.descricao}</span>
                    </div>
                  )}
                </div>
              )}
              {viewType === 'clientes' && (
                <div className="view-grid">
                  <div className="view-field">
                    <span className="view-label">Nome</span>
                    <span className="view-value">{viewItem.nome}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Email</span>
                    <span className="view-value">{viewItem.email || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Telefone</span>
                    <span className="view-value">{viewItem.telefone || '-'}</span>
                  </div>
                  {viewItem.endereco && (
                    <div className="view-field view-field-full">
                      <span className="view-label">Endereço</span>
                      <span className="view-value">{viewItem.endereco}</span>
                    </div>
                  )}
                </div>
              )}
              {viewType === 'fornecedores' && (
                <div className="view-grid">
                  <div className="view-field">
                    <span className="view-label">Nome</span>
                    <span className="view-value">{viewItem.nome}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">CNPJ</span>
                    <span className="view-value">{viewItem.cnpj || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Contato</span>
                    <span className="view-value">{viewItem.contato || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Email</span>
                    <span className="view-value">{viewItem.email || '-'}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Telefone</span>
                    <span className="view-value">{viewItem.telefone || '-'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showLoteModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>{editingLote ? 'Editar Lote' : 'Novo Lote'}</h3>
              <button onClick={() => setShowLoteModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nome do Lote *</label>
                <input type="text" value={loteForm.nome} onChange={e => setLoteForm({ ...loteForm, nome: e.target.value })} autoFocus />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <input type="text" value={loteForm.descricao} onChange={e => setLoteForm({ ...loteForm, descricao: e.target.value })} />
              </div>
              {lotes.length > 0 && (
                <div className="form-group">
                  <label>Lotes existentes</label>
                  <div className="lotes-list">
                    {lotes.map(l => (
                      <div key={l.id} className="lote-item">
                        <span>{l.nome}</span>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button className="pasta-action-btn" onClick={() => { setEditingLote(l); setLoteForm({ nome: l.nome, descricao: l.descricao || '' }); }}>
                            <Edit size={13} />
                          </button>
                          {canDelete && (
                            <button className="pasta-action-btn pasta-action-delete" onClick={() => deleteLote(l.id)}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setShowLoteModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={saveLote}>
                <Save size={16} /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {showUserManagement && (
        <UserManagement user={user} onBack={() => setShowUserManagement(false)} />
      )}

      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

export default App;
